'use client';

import { createRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import nProgress from 'nprogress';

import DateFormat from './DateFormat';
import { Author, ContentDocument, CoverImage } from 'src/services/keystone-types';
import { authorPageUrl } from 'src/services/route-utils';
import ExtendedContentRenderer from './ExtendedContentRenderer';
import TableOfContents from './TableOfContents';

type Props = {
    createdAt?: string;
    updatedAt?: string;
    author?: Partial<Author>;
    image?: Partial<CoverImage>;
    title: string;
    renderTitleAsH1?: boolean;
    content?: ContentDocument;
    rawBody?: string;
    includeTableOfContentsOnMobile?: boolean;
};

export default function ArticleWrapper({
    title,
    renderTitleAsH1 = true,
    rawBody = '',
    includeTableOfContentsOnMobile = false,
    ...props
}: Props) {
    const elementRef = createRef<HTMLDivElement>();

    const wordCount = rawBody.trim().split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / 250);

    useEffect(() => {
        nProgress.configure({
            minimum: 0,
            trickle: false,
        });

        let oldProgress = 0;

        const documentScroll = () => {
            const element = elementRef.current;
            if (!element) return;

            const rect = element.getBoundingClientRect();

            const top = -rect.top;
            let progress = 0;
            if (top >= rect.height) {
                progress = 1;
            } else {
                progress = Math.max(0, top / rect.height);
            }
            if (oldProgress !== progress) {
                nProgress.set(progress);
                oldProgress = progress;
            }
        };

        window.addEventListener('scroll', documentScroll);
        window.addEventListener('resize', documentScroll);

        return () => {
            window.removeEventListener('scroll', documentScroll);
            window.removeEventListener('resize', documentScroll);
            nProgress.configure({
                minimum: 0.1,
                trickle: true,
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const introductionSection: ContentDocument = { document: [] };
    const articleBody: ContentDocument = { document: props.content?.document || [] };
    if (includeTableOfContentsOnMobile) {
        const firstHeadingIndex = props.content?.document.findIndex(
            (item) => item.type === 'heading' && (item.level || 0) > 1,
        );
        if (firstHeadingIndex !== -1) {
            introductionSection.document = props.content?.document.slice(0, firstHeadingIndex) || [];
            articleBody.document = props.content?.document.slice(firstHeadingIndex) || [];
        }
    }

    return (
        <article className="article" ref={elementRef}>
            {renderTitleAsH1 ? (
                <h1>{title}</h1>
            ) : (
                <div className="font-display p-4 pt-8 text-3xl md:px-8 md:text-6xl">{title}</div>
            )}
            {props.image && props.image.image && (
                <Image
                    src={props.image.image}
                    alt={title}
                    className="w-full"
                    width={props.image.image_width || 1000}
                    height={props.image.image_height || 1000}
                    sizes="(max-width: 640px) 320px, (max-width: 768px) 384px, (max-width: 840px) 420px, (max-width: 1024px) 512px, (max-width: 1280px) 640px, 768px"
                    priority
                    placeholder={props.image.placeholder}
                />
            )}
            <section className="gap-8 p-4 text-xs md:flex md:px-8 md:text-sm">
                <div className="flex-1">
                    Timp de citire: {readingTime} {readingTime > 1 ? 'minute' : 'minut'}
                </div>
                <div>
                    {props.author && (
                        <>
                            Autor: <Link href={authorPageUrl(props.author)}>{props.author.name}</Link>
                        </>
                    )}
                </div>

                {props.createdAt && (
                    <div>
                        Publicat: <DateFormat date={props.createdAt} />
                    </div>
                )}
                {props.updatedAt && (
                    <div>
                        Ultima actualizare: <DateFormat date={props.updatedAt} />
                    </div>
                )}
            </section>

            {includeTableOfContentsOnMobile && (
                <>
                    <ExtendedContentRenderer content={introductionSection} />
                    <div className="pt-8 lg:hidden">
                        <TableOfContents document={articleBody.document} hasActiveIndicator={false} />
                    </div>
                </>
            )}
            <ExtendedContentRenderer content={articleBody} />
        </article>
    );
}
